import { unitFor, useLocaleContext } from "../contexts/LocaleContext";

import { useEffect, useState } from "react";
import fetchChart from "../api/ng/chart";
import convert, { isUnit, normalizeFlowRateUnit, Units } from "../convert";
import { ISelectedSensor } from "../models/SelectedSensor";
import { SensorDisplayPrecip } from "../models/SensorDisplay";
import { isoToSeconds, toSeconds } from "../shared/duration";

export interface ChartApiResponseMeta {
    units: { [measure: string]: Units };
}
export interface ChartApiResponseData {
    ts: string;
    // would like value of measure index signature to be number, but:
    // https://www.typescriptlang.org/docs/handbook/2/objects.html#index-signatures
    [measure: string]: any;
}

export interface ChartApiResponse {
    meta: ChartApiResponseMeta;
    data: ChartApiResponseData[];
}

export type Datum = {
    ts: Date;
    value: number;
}

export interface ChartData {
    data: Datum[];
    unit: Units;
}

const useChartData = (selected: ISelectedSensor) => {
    const { units } = useLocaleContext();
    const { display } = selected;
    const toUnit = unitFor(units, display.measure);

    const [response, setResponse] = useState<ChartApiResponse>()

    useEffect(() => {
        const fetchData = async () => {
            const response = await fetchChart(selected);
            setResponse(response);
        }
        fetchData();
    }, [selected]);

    if (response) {
        const dataMeasure = SensorDisplayPrecip.is(display) ? "precip" : display.measure;
        const { meta, data } = response;

        const measure_ = Object.keys(meta.units).find(k => k.startsWith(dataMeasure));
        if (measure_ === undefined) {
            setResponse(undefined);
            return;
        }

        const multiplier = display.measure === "intensity"
            ? toSeconds({ hours: 1 }) / isoToSeconds(display.interval)
            : 1;

        let fromUnit: Units;
        switch (display.measure) {
            case "intensity":
                fromUnit = "ft3/s"
                break;
            case "discharge":
                // shouldn't need this, but: https://github.com/mobxjs/mobx-state-tree/issues/2268
                fromUnit = normalizeFlowRateUnit(meta.units[measure_])
                break;
            default:
                const unit = meta.units[measure_];
                if (!isUnit(unit)) {
                    throw new Error(`Unhandled unit ${unit}`);
                }
                fromUnit = unit;
        }

        const measureData: Datum[] = data.map((d) => ({
            ts: new Date(d.ts),
            value: convert(d[dataMeasure] * multiplier).from(fromUnit).to(toUnit)
        }));

        const chartData: ChartData = {
            data: measureData,
            unit: toUnit
        };
        return chartData;
    }
};

export default useChartData;
